// src/pages/ClassroomInsights.js

import React, { useState, useEffect } from "react";
import { Bar, Pie } from "react-chartjs-2";
import "chart.js/auto";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import "../App.css";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useMediaQuery } from "@mui/material";

Chart.register(ChartDataLabels); // Register globally

const ClassroomInsights = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("All");
  const [activeTab, setActiveTab] = useState("charts");
  const [timeRange, setTimeRange] = useState("All");
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const schoolId = localStorage.getItem("schoolId");
        if (!schoolId) return;

        const q = query(collection(db, "students"), where("schoolId", "==", schoolId));
        const snapshot = await getDocs(q);

        const allStudents = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.firstName) {
            if (!data.className) data.className = "Unassigned";
            allStudents.push(data);
          }
        });

        setStudents(allStudents);
      } catch (error) {
        console.error("Error fetching classroom insights:", error);
      }
    };

    fetchStudentData();
  }, []);

  const classes = [...new Set(students.map((s) => s.className))];
  const now = new Date();

  // Helper: find a student's most recent activity date (lastRead or latest readingActivity entry)
  function latestActivityDate(s) {
    if (s.lastRead) {
      const d = new Date(s.lastRead);
      if (!isNaN(d)) return d;
    }
    const ra = Array.isArray(s.readingActivity) ? s.readingActivity : [];
    if (!ra.length) return null;

    const ms = ra
      .map((e) => {
        if (e?.date) {
          const d = new Date(e.date);
          return d.getTime();
        }
        // tolerant of alternate shapes
        if (typeof e?.ts === "number") return e.ts;
        if (typeof e?.time === "number") return e.time;
        return NaN;
      })
      .filter(Number.isFinite);

    if (!ms.length) return null;
    return new Date(Math.max(...ms));
  }

  const filtered = students.filter((s) => {
    if (selectedClass !== "All" && s.className !== selectedClass) return false;
    if (timeRange === "All") return true;

    const last = latestActivityDate(s);
    if (!last) return false;

    const daysAgo = (now - last) / (1000 * 60 * 60 * 24);
    return (timeRange === "7d" && daysAgo <= 7) || (timeRange === "30d" && daysAgo <= 30);
  });

  // If viewing All Classes, cap charts to Top 25 by storiesRead to keep charts readable.
// If viewing a specific class, show all students in that class (expected <= ~30).
const chartStudents =
  selectedClass === "All"
    ? [...filtered]
        .sort((a, b) => (b.storiesRead || 0) - (a.storiesRead || 0))
        .slice(0, 25)
    : filtered;


  const storiesPerStudent = {
  labels: chartStudents.map((s) => `${s.firstName} ${s.lastName}`),
  datasets: [
    {
      label: "Stories Read",
      data: chartStudents.map((s) => s.storiesRead || 0),
      backgroundColor: "rgba(0, 123, 255, 0.7)",
      hoverOffset: 10,
    },
  ],
};

const timeSpentData = {
  labels: chartStudents.map((s) => `${s.firstName} ${s.lastName}`),
  datasets: [
    {
      label: "Time Spent Reading (mins)",
      data: chartStudents.map((s) => Math.round((s.totalReadingTime || 0) / 60)),
      backgroundColor: "rgba(255, 159, 64, 0.6)",
      hoverOffset: 10,
    },
  ],
};

  const genreTotals = {};
  filtered.forEach((s) => {
    if (s.genresExplored) {
      Object.entries(s.genresExplored).forEach(([genre, count]) => {
        genreTotals[genre] = (genreTotals[genre] || 0) + count;
      });
    }
  });

  const sortedGenreEntries = Object.entries(genreTotals).sort((a, b) => b[1] - a[1]);
  const genresExplored = {
    labels: sortedGenreEntries.map((entry) => entry[0]),
    datasets: [
      {
        label: "Genres Explored",
        data: sortedGenreEntries.map((entry) => entry[1]),
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
        ],
        hoverOffset: 10,
      },
    ],
  };

  const engagementLevels = {
  labels: chartStudents.map((s) => `${s.firstName} ${s.lastName}`),
  datasets: [
    {
      label: "Stories Read",
      data: chartStudents.map((s) => s.storiesRead || 0),
      backgroundColor: "rgba(54, 162, 235, 0.6)",
      hoverOffset: 10,
    },
    {
      label: "Time Spent Reading (mins)",
      data: chartStudents.map((s) => Math.round((s.totalReadingTime || 0) / 60)),
      backgroundColor: "rgba(255, 206, 86, 0.6)",
      hoverOffset: 10,
    },
  ],
};

  // Avoid mutating "filtered" when producing topStudents
  const topStudents = [...filtered]
    .sort((a, b) => (b.storiesRead || 0) - (a.storiesRead || 0))
    .slice(0, 5);

  const topStudentsChart = {
    labels: topStudents.map((s) => `${s.firstName} ${s.lastName}`),
    datasets: [
      {
        label: "Top Stories Read",
        data: topStudents.map((s) => s.storiesRead || 0),
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  // Chart options (flip to horizontal bars if many students or on mobile)
  const many = chartStudents.length > 12 || isMobile;


  const storiesPerStudentOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: many ? "y" : "x",
  plugins: { datalabels: { display: false } },
  scales: {
    x: {
      beginAtZero: true,
      title: { display: true, text: many ? "Stories" : "Students" },
    },
    y: {
      ticks: {
        autoSkip: false,
        font: { size: 12 },
      },
    },
  },
};

const timeSpentOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: many ? "y" : "x",
  plugins: { datalabels: { display: false } },
  scales: {
    x: {
      beginAtZero: true,
      title: { display: true, text: many ? "Minutes" : "Students" },
    },
    y: {
      ticks: {
        autoSkip: false,
        font: { size: 12 },
      },
    },
  },
};



  if (students.length === 0) {
    return (
      <div
        className="page-container"
        style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}
      >
        <div className="spinner"></div>
      </div>
    );
  }

  const handleDownloadPDF = () => {
    const elementId = activeTab === "top" ? "top-students-container" : "charts-container";
    const element = document.getElementById(elementId);
    if (!element) return;

    html2canvas(element).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * pageWidth) / canvas.width;

      const titleText =
        activeTab === "top"
          ? "Top Students - Classroom Insights"
          : selectedClass === "All"
          ? "All Classes - Classroom Insights"
          : `${selectedClass} - Classroom Insights`;

      const fileName = `${
        activeTab === "top" ? "Top_Students" : selectedClass === "All" ? "All_Classes" : selectedClass.replace(/\s+/g, "_")
      }_Insights_${new Date().toISOString().slice(0, 10)}.pdf`;

      pdf.setFontSize(14);
      pdf.setFont(undefined, "bold");
      const textWidth = pdf.getTextWidth(titleText);
      const xCenter = (pageWidth - textWidth) / 2;
      const yTitle = 12;

      pdf.text(titleText, xCenter, yTitle);
      pdf.line(xCenter, yTitle + 1, xCenter + textWidth, yTitle + 1);

      const yStart = 20;
      const available = pageHeight - yStart - 10;
      const h = Math.min(imgHeight, available);
      pdf.addImage(imgData, "PNG", 0, yStart, imgWidth, h);

      pdf.save(fileName);
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        overflowX: "auto", // safety net for wide layouts
      }}
    >
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px" }}>
          <button onClick={() => navigate("/help")} className="header-button">
            Help
          </button>
          <button onClick={() => navigate("/school-dashboard")} className="header-button">
            Back to Dashboard
          </button>
        </nav>
      </header>

      <main style={{ padding: "20px", overflowX: "auto" }}>
        <h1
          style={{
            fontSize: "28px",
            fontWeight: "bold",
            fontFamily: "Montserrat, sans-serif",
            color: "#007BFF",
            textAlign: "center",
          }}
        >
          Classroom Insights
        </h1>

        <div
          style={{
            background: "rgba(255,255,255,0.9)",
            padding: "20px",
            borderRadius: "10px",
            margin: "20px auto",
            maxWidth: "900px",
          }}
        >
          <p style={{ marginBottom: "10px" }}>
            Use this dashboard to view insights into your students' reading habits, genre interests, and time spent
            reading. Use the tabs to switch between detailed charts and a summary of top students.
          </p>
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
              <label>
                <strong>Filter by Class:</strong>
              </label>
              <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                <option value="All">All Classes</option>
                {classes.map((cls, i) => (
                  <option key={i} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>

              <label>
                <strong>Filter by Time:</strong>
              </label>
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="All">All Time</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
            </div>

            <button
              onClick={handleDownloadPDF}
              style={{
                padding: "6px 12px",
                backgroundColor: "#007BFF",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Download PDF of Charts
            </button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px", gap: "15px" }}>
          <button
            onClick={() => handleTabChange("charts")}
            style={{
              padding: "10px 20px",
              backgroundColor: activeTab === "charts" ? "#007BFF" : "#eee",
              color: activeTab === "charts" ? "white" : "#333",
              border: "none",
              borderRadius: "5px",
            }}
          >
            Charts
          </button>
          <button
            onClick={() => handleTabChange("top")}
            style={{
              padding: "10px 20px",
              backgroundColor: activeTab === "top" ? "#007BFF" : "#eee",
              color: activeTab === "top" ? "white" : "#333",
              border: "none",
              borderRadius: "5px",
            }}
          >
            Top Students
          </button>
        </div>

        {activeTab === "charts" && (
          <div id="charts-container">
           <p
  style={{
    marginTop: 0,
    marginBottom: 12,
    color: "#ffffff",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: 500,
    textShadow: "0 1px 2px rgba(0,0,0,0.6)",
  }}
>
  {selectedClass === "All"
    ? "Showing top 25 students by stories read. Select a class to see all students in that class."
    : `Showing all students in ${selectedClass}.`}
</p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", // stack on phones
                gap: "20px",
                marginTop: "20px",
              }}
            >

              {/* Stories Read Per Student */}
              <div
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "10px",
                  height: many ? "520px" : "300px",
justifyContent: "flex-start",
                  display: "flex",
                  flexDirection: "column",
                  
                }}
              >
                <h2>Stories Read Per Student</h2>
                <div
  style={{
    height: many ? Math.min(900, Math.max(320, chartStudents.length * 28)) : 200,
    overflowY: many ? "auto" : "hidden",
  }}
>
  <Bar data={storiesPerStudent} options={storiesPerStudentOptions} />
</div>


              </div>

              {/* Time Spent Reading */}
              <div
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "10px",
                  height: many ? "520px" : "300px",
justifyContent: "flex-start",
                  display: "flex",
                  flexDirection: "column",
                  
                }}
              >
                <h2>Time Spent Reading</h2>
                <div
  style={{
    height: many ? Math.min(900, Math.max(320, chartStudents.length * 28)) : 200,
    overflowY: many ? "auto" : "hidden",
  }}
>
  <Bar data={timeSpentData} options={timeSpentOptions} />
</div>


              </div>
            </div>

            {/* Genres Explored */}
            <div style={{ marginTop: "30px", background: "#fff", padding: "20px", borderRadius: "10px" }}>
              <h2>Genres Explored</h2>
              <div style={{ width: isMobile ? "100%" : "400px", margin: "0 auto" }}>
                <Pie
                  data={genresExplored}
                  options={{
                    plugins: {
                      datalabels: {
                        formatter: (value, context) => {
                          const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                          const percentage = total ? ((value / total) * 100).toFixed(1) : 0;
                          return `${percentage}%`;
                        },
                        color: "#fff",
                        font: { weight: "bold", size: 14 },
                        anchor: "center",
                        align: "center",
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const genre = context.label;
                            const count = context.raw;
                            return `${genre}: ${count} stories`;
                          },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>

            {/* Engagement Levels */}
            <div
              style={{ marginTop: "30px", background: "#fff", padding: "20px", borderRadius: "10px", height: "400px" }}
            >
              <h2>Engagement Levels</h2>
              <div style={{ height: "300px" }}>
                <Bar
                  data={engagementLevels}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { datalabels: { display: false } },
                    scales: {
                      x: { title: { display: true, text: "Students" } },
                      y: { beginAtZero: true, title: { display: true, text: "Stories / Time (mins)" } },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Top Students Chart and Table */}
        {activeTab === "top" && (
          <div id="top-students-container" style={{ padding: "10px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", // stack on phones
                gap: "20px",
                marginTop: "20px",
              }}
            >
              <div style={{ background: "#fff", padding: "20px", borderRadius: "10px" }}>
                <h2>Top Students by Stories Read</h2>
                <div style={{ maxHeight: "450px", overflowY: "auto" }}>
                  <Bar
                    data={topStudentsChart}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { datalabels: { display: false } },
                    }}
                  />
                </div>
              </div>
              <div style={{ background: "#fff", padding: "20px", borderRadius: "10px" }}>
                <h2>Top Students Table</h2>
                <table
                  style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Montserrat, sans-serif" }}
                >
                  <thead>
                    <tr>
                      <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Name</th>
                      <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Class</th>
                      <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Stories Read</th>
                      <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>Time Spent (mins)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topStudents.map((s, i) => (
                      <tr key={i}>
                        <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                          {s.firstName} {s.lastName}
                        </td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{s.className}</td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>{s.storiesRead || 0}</td>
                        <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                          {Math.round((s.totalReadingTime || 0) / 60)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="page-footer">
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: "10px" }}>
          <a href="/about" className="footer-link">
            About Us
          </a>
          <a href="/contact" className="footer-link">
            Contact
          </a>
        </nav>
      </footer>
    </div>
  );
};

export default ClassroomInsights;
